// WHAT GOES IN THE EXPORT, ASKED BEFORE IT RUNS.
//
// Owner, 2026-10-22: "an option on the export formats on the issue manager to
// have a popup asking based on the person permissions, to check box hours in
// the report and check box prices in the report, and these can only [be]
// offered to users who have permissions - if a user has hours it can offer
// him that option, if a user has prices it offers him the check box for
// prices, and if a user has both it offers both check boxes."
//
// HOURS ONLY. Prices stay in the contractor hours and cost report - the
// owner's decision, twice (see below).
//
// SO THE SERVER DECIDES WHAT IS OFFERED, not this file. It answers
// GET /issue/export/options with { hours, prices } for this person on this
// project, and only a true comes back as a box. A person with neither grant
// gets NO DIALOG AT ALL - asking them to confirm a choice they do not have is
// a worse experience than the one they have today.
//
// And the tick is a preference, not a key: the export re-checks the same
// permissions server-side (utils/exportOptions.includeFor), so an unticked
// box takes something out and a ticked one can never put something in that
// the person could not already see.
//
// Its own file because IssueExportMenu is a menu, and because the issues page
// is past the hooks lint's path budget - see feedback_hooks_lint_path_limit.
import React from "react";
import { Modal, Checkbox, Space } from "antd";

/** A plain async confirm: resolves { hours, prices } or null if cancelled. */
export function askExportOptions({ allowed, formatName }) {
  const offerHours = !!(allowed && allowed.hours);
  // Estimated hours is shown to everyone today, so its tick is offered to
  // everyone - it takes a column OUT, it never adds one.
  const offerEstimates = !(allowed && allowed.estimates === false);
  // PRICES ARE NOT ON THIS DIALOG, AND THAT IS THE DECISION, not a gap.
  //
  // Owner, 2026-10-08: "on issues just show the hours don't show the cost in
  // currency leave that for specialized export" - and asked again on
  // 2026-10-22 whether to reverse it, he said no: "just keep the hours as a
  // tick box during export popup confirmation, and don't add prices back, we
  // will keep prices to contractors and cost".
  //
  // So money lives in ONE place, the contractor hours and cost report, which
  // can carry rate overrides, per-system totals and an honest statement of
  // what is unpriced. An issue register is a list of work; pricing it per row
  // invites "is this what we bill?" of a document that was never an invoice.
  //
  // The costView permission and the server-side narrowing stay (utils/
  // exportOptions): they are what the contractor report reads, and they are
  // what stops a hand-made includePrices=true meaning anything here.
  const offerPrices = false;

  // Nothing to choose - do not stop the person to tell them so.
  if (!offerHours && !offerPrices && !offerEstimates) return Promise.resolve({ hours: false, estimates: true, prices: false });

  // The defaults match what the export did before this dialog existed, so
  // pressing Export without reading changes nothing about today's document.
  const picked = { hours: offerHours, estimates: true, prices: false };

  return new Promise((resolve) => {
    const modal = Modal.confirm({
      title: `What should the ${formatName} include?`,
      icon: null,
      okText: "Export",
      cancelText: "Cancel",
      content: (
        <Space direction="vertical" size={6} style={{ marginTop: 8 }}>
          <div style={{ color: "#6B7C86", fontSize: 12.5 }}>
            You are offered only what you are allowed to see.
          </div>
          {/* TWO KINDS OF TIME, TWO TICKS (owner, 2026-10-22: "estimated
              hours need to have its check box similar to resolution hours").
              Hours spent is what the work took; estimated hours is what it
              was expected to take, and a reader often wants one without the
              other. One tick for both is what made the first version read as
              broken - unticking it left the estimate column standing. */}
          {offerHours ? (
            <Checkbox
              defaultChecked
              data-testid="export-include-hours"
              onChange={(e) => { picked.hours = e.target.checked; }}
            >
              Hours spent (resolution hours)
            </Checkbox>
          ) : null}
          {offerEstimates ? (
            <Checkbox
              defaultChecked
              data-testid="export-include-estimates"
              onChange={(e) => { picked.estimates = e.target.checked; }}
            >
              Estimated hours
            </Checkbox>
          ) : null}
          {offerPrices ? (
            <Checkbox
              data-testid="export-include-prices"
              onChange={(e) => { picked.prices = e.target.checked; }}
            >
              Prices in the report
            </Checkbox>
          ) : null}
        </Space>
      ),
      onOk: () => resolve({ hours: picked.hours, estimates: picked.estimates, prices: picked.prices }),
      onCancel: () => resolve(null),
    });
    // antd keeps the instance alive after the test that opened it; the menu
    // destroys it on unmount so one dialog cannot answer the next export.
    askExportOptions._last = modal;
  });
}

export default askExportOptions;
